import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Checkbox,
  Drawer,
  InputRef,
  Select,
  Space,
  Upload,
} from "antd";
import { Button, Form, Input, Popconfirm, Table } from "antd";
import type { FormInstance } from "antd/es/form";
import {
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
} from "@ant-design/icons";
import {
  DATA_TYPES,
  DRAWER_PLACEMENTS,
  NOTIFICATION_TYPES,
} from "../../../../../config/constants";
import { UploadChangeParam, UploadFile } from "antd/lib/upload";
import TablesService from "../../../../../services/tables/TablesService";
import saveAs from "file-saver";
import NotificationsUtil from "../../../../shared/utils/NotificationsUtil";
import { useTranslation } from "react-i18next";
import { GetTablesRequest } from "../../../../../models/tables/GetTablesRequest";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { ValidateStatus } from "antd/lib/form/FormItem";
import { GetFieldsRequest } from "../../../../../models/fields/GetFieldsRequest";
import { FIELD_MAX_LENGTH } from "../../../../../config/consts/validations";
import { FieldType } from "../../../../../models/fields/FieldType";
import { FieldSubType } from "../../../../../models/fields/FieldSubType";

const EditableContext = React.createContext<FormInstance<any> | null>(null);


enum RowAction {
  UploadFromExcel = 1,
  UploadFromTable,
}

interface Item {
  key: string;
  fieldName: string;
  datatype: string;
  maxLength: number;
  autotrim: boolean;
  precision: number;
  scale: number;
  label: string;
  description: string;
  deltaCollection: string;
  mandatory: boolean;
  owner?: string;
  whoToSolve?: string;
  position: number;
  nonEditable: boolean;
  typeId?: number;
  subtypeFormatId?: number;

  fieldNameValidationStatus: ValidateStatus;
  datatypeValidationStatus: ValidateStatus;
  maxLengthValidationStatus: ValidateStatus;
  autotrimValidationStatus: ValidateStatus;
  precisionValidationStatus: ValidateStatus;
  scaleValidationStatus: ValidateStatus;
  labelValidationStatus: ValidateStatus;
  descriptionValidationStatus: ValidateStatus;
  deltaCollectionValidationStatus: ValidateStatus;
  mandatoryValidationStatus: ValidateStatus;
  positionValidationStatus: ValidateStatus;
}

interface EditableRowProps {
  index: number;
}

const EditableRow: React.FC<EditableRowProps> = ({ index, ...props }) => {
  const [form] = Form.useForm();

  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

interface EditableCellProps {
  title: React.ReactNode;
  editable: boolean;
  children: React.ReactNode;
  dataIndex: keyof Item;
  record: Item;
  fieldTypes: FieldType[];
  fieldSubTypes: FieldSubType[];
  selectedOperation: number;
  handleSave: (record: Item) => void;
}

const EditableCell: React.FC<EditableCellProps> = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  selectedOperation,
  fieldTypes,
  fieldSubTypes,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<InputRef>(null);
  const positionInputRef = useRef<InputRef>(null);


  const form = useContext(EditableContext)!;
  const { t, i18n } = useTranslation("translations");

  useEffect(() => {
    if (editing) {
      inputRef?.current?.focus();
      positionInputRef?.current?.focus();
      inputRef?.current?.select();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);

    form.setFieldsValue({
      fieldName: record.fieldName,
      datatype: record.datatype,
      maxLength: record.maxLength,
      autotrim: record.autotrim,
      precision: record.precision,
      scale: record.scale,
      owner: record.owner,
      whoToSolve: record.whoToSolve,
      label: record.label,
      description: record.description,
      deltaCollection: record.deltaCollection,
      mandatory: record.mandatory,
      position: record.position,
    });
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      handleSave({ ...record, ...values });
    } catch (errInfo) {
      console.log("Save failed:", errInfo);
    }
  };

  const onFieldNameChange = (event: any, record: any, dataIndex: any) => {
    form.setFieldsValue({
      fieldName: event.target.value,
      datatype: record.datatype,
      maxLength: record.maxLength,
      autotrim: record.autotrim,
      precision: record.precision,
      scale: record.scale,
      owner: record.owner,
      whoToSolve: record.whoToSolve,
      label: record.label,
      description: record.description,
      deltaCollection: record.deltaCollection,
      mandatory: record.mandatory,
      position: record.position,
    });
    save();
  };

  const onOwnerChange = (valueOrEvent: any, record: any, dataIndex: any) => {
    const value = valueOrEvent?.target ? valueOrEvent.target.value : valueOrEvent;
    form.setFieldsValue({
      ...record,
      owner: value,
    });
    save();
  };

  const onWhoToSolveChange = (valueOrEvent: any, record: any, dataIndex: any) => {
    const value = valueOrEvent?.target ? valueOrEvent.target.value : valueOrEvent;
    form.setFieldsValue({
      ...record,
      whoToSolve: value,
    });
    save();
  };

  const onPositionChange = (event: any, record: any, dataIndex: any) => {
    let newRecord = record;
    if (event?.target.value) {
      newRecord.position = parseInt(event?.target.value);
      handleSave({ ...newRecord });
    }
  };

  const onMaxLengthChange = (event: any, record: any, dataIndex: any) => {
    let value = event?.target.value;

    // only allow positive values
    if (value === "" || /^\d*[0-9]\d*$/.test(value)) {
      let newRecord = record;

      if (record.datatype.toLowerCase() === "varchar" && value > FIELD_MAX_LENGTH.TYPE_VARCHAR) {
        value = FIELD_MAX_LENGTH.TYPE_VARCHAR
      }
      if (record.datatype.toLowerCase() === "nvarchar" && value > FIELD_MAX_LENGTH.TYPE_NVARCHAR) {
        value = FIELD_MAX_LENGTH.TYPE_NVARCHAR
      }

      newRecord.maxLength = value;
      handleSave({ ...newRecord });
    }
  };

  const onPrecisionChange = (event: any, record: any, dataIndex: any) => {
    let value = event?.target.value;
    if (value > 32) {
      return;
    }

    let newRecord = record;
    newRecord.precision = value;
    handleSave({ ...newRecord });
  };

  const onScaleChange = (event: any, record: any, dataIndex: any) => {
    let value = event?.target.value;
    if (value > 12) {
      return;
    }

    let newRecord = record;
    newRecord.scale = value;
    handleSave({ ...newRecord });
  };

  const onDatatypeChange = (value: any) => {
    form.setFieldsValue({
      fieldName: record.fieldName,
      datatype: value,
      maxLength: record.maxLength,
      autotrim: record.autotrim,
      precision: record.precision,
      scale: record.scale,
      owner: record.owner,
      whoToSolve: record.whoToSolve,
      label: record.label,
      description: record.description,
      deltaCollection: record.deltaCollection,
      mandatory: record.mandatory,
      position: record.position,
    });

    let newRecord = record;
    newRecord.datatype = value;
    handleSave({ ...newRecord });
  };

  const onFieldTypeChange = (value: number) => {
    let newRecord = { ...record, typeId: value };
    handleSave({ ...newRecord });
  }

  const onSubFieldTypeChange = (value: number) => {
    let newRecord = { ...record, subtypeFormatId: value };
    handleSave({ ...newRecord });
  }

  const onMandatoryChange = (event: CheckboxChangeEvent, record: any, dataIndex: any) => {
    let newRecord = record;
    newRecord.mandatory = !newRecord.mandatory;
    handleSave({ ...newRecord });
  };

  const onAutotrimChange = (event: CheckboxChangeEvent, record: any, dataIndex: any) => {
    let newRecord = record;
    newRecord.autotrim = !newRecord.autotrim;
    handleSave({ ...newRecord });
  };

  const onLabelChange = (value: string, record: any, dataIndex: any) => {
    form.setFieldsValue({
      fieldName: record.fieldName,
      datatype: record.datatype,
      maxLength: record.maxLength,
      autotrim: record.autotrim,
      precision: record.precision,
      scale: record.scale,
      label: value,
      description: record.description,
      deltaCollection: record.deltaCollection,
      mandatory: record.mandatory,
      position: record.position,
    });
    save();
  };

  const onDescriptionChange = (value: string, record: any, dataIndex: any) => {
    form.setFieldsValue({
      fieldName: record.fieldName,
      datatype: record.datatype,
      maxLength: record.maxLength,
      autotrim: record.autotrim,
      precision: record.precision,
      scale: record.scale,
      owner: record.owner,
      whoToSolve: record.whoToSolve,
      label: record.label,
      description: value,
      deltaCollection: record.deltaCollection,
      mandatory: record.mandatory,
      position: record.position,
    });
    save();
  };

  const onDeltaCollectionChange = (value: string, record: any, dataIndex: any) => {
    form.setFieldsValue({
      fieldName: record.fieldName,
      datatype: record.datatype,
      maxLength: record.maxLength,
      autotrim: record.autotrim,
      precision: record.precision,
      scale: record.scale,
      owner: record.owner,
      whoToSolve: record.whoToSolve,
      label: record.label,
      description: record.description,
      deltaCollection: value,
      mandatory: record.mandatory,
      position: record.position,
    });
    save();
  };



  const getValidationStatus = (record: any, dataIndex: any) => {
    if (dataIndex == "fieldName") {
      if (record[dataIndex] === "") {
        return "error";
      }
      return "success";
    }
    if (dataIndex == "datatype") {
      if (record[dataIndex] === "") {
        return "error";
      }
      return "success";
    }
    if (dataIndex == "maxLength") {
      if (record[dataIndex] === "") {
        return "error";
      }
      return "success";
    }
    if (dataIndex == "autotrim") {
      return "success";
    }
    if (dataIndex == "precision") {
      if (record[dataIndex] === "") {
        return "error";
      }
      return "success";
    }
    if (dataIndex == "scale") {
      if (record[dataIndex] === "") {
        return "error";
      }
      return "success";
    }
    if (dataIndex == "label") {
      return "success";
    }
    if (dataIndex == "description") {
      return "success";
    }
    if (dataIndex == "deltaCollection") {
      return "success";
    }
    if (dataIndex == "mandatory") {
      return "success";
    }
    if (dataIndex == "owner") {
      return "success";
    }
    if (dataIndex == "whoToSolve") {
      return "success";
    }
    if (dataIndex == "position") {
      if (record[dataIndex] === "") {
        return "error";
      }
      return "success";
    }
  }

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form.Item
        shouldUpdate={true}
        style={{ margin: 0, height: 22 }}
        name={dataIndex}
        validateStatus={getValidationStatus(record, dataIndex)}
      >
        {dataIndex === "fieldName" && (
          <Input
            style={{ margin: 0, paddingBottom: 0, paddingTop: 0 }}
            onChange={(event) => onFieldNameChange(event, record, dataIndex)}
            onPressEnter={() => { save(); toggleEdit(); }}
            onBlur={() => {
              save(); toggleEdit();
            }}
            ref={inputRef}
            value={record[dataIndex]}
            defaultValue={record[dataIndex]}
            maxLength={FIELD_MAX_LENGTH.NAME}
          />
        )}
        {dataIndex === "maxLength" && (
          <Input
            style={{ margin: 0, paddingBottom: 0, paddingTop: 0 }}
            disabled={record["nonEditable"]}
            onChange={(event) => onMaxLengthChange(event, record, dataIndex)}
            onPressEnter={() => { save(); toggleEdit(); }}
            onBlur={() => {
              save(); toggleEdit();
            }}
            value={record[dataIndex]}
            defaultValue={record[dataIndex]}
            size={"small"}
            ref={inputRef}
            min={0}
            max={record["datatype"]?.toLowerCase() === "varchar" ? FIELD_MAX_LENGTH.TYPE_VARCHAR : FIELD_MAX_LENGTH.TYPE_NVARCHAR}
          />
        )}
        {dataIndex === "precision" && (
          <Input
            style={{ margin: 0, paddingBottom: 0, paddingTop: 0 }}
            disabled={record["nonEditable"]}
            onChange={(event) => onPrecisionChange(event, record, dataIndex)}
            onPressEnter={() => { save(); toggleEdit(); }}
            onBlur={() => {
              save(); toggleEdit();
            }}
            value={record[dataIndex]}
            defaultValue={record[dataIndex]}
            min={0}
            max={32}
            size={"small"}
            type={"number"}
            ref={inputRef}
          />
        )}
        {dataIndex === "scale" && (
          <Input
            style={{ margin: 0, paddingBottom: 0, paddingTop: 0 }}
            disabled={record["nonEditable"]}
            onChange={(event) => onScaleChange(event, record, dataIndex)}
            onPressEnter={() => { save(); toggleEdit(); }}
            onBlur={() => {
              save(); toggleEdit();
            }}
            value={record[dataIndex]}
            defaultValue={record[dataIndex]}
            min={0}
            max={12}
            size={"small"}
            type={"number"}
            ref={inputRef}
          />
        )}
        {(dataIndex === "label") && (
          <Input
            style={{ margin: 0, paddingBottom: 0, paddingTop: 0 }}
            onChange={(event) => onLabelChange(event.target.value, record, dataIndex)}
            onPressEnter={() => { save(); toggleEdit(); }}
            onBlur={() => {
              save(); toggleEdit();
            }}
            ref={inputRef}
            value={record[dataIndex]}
            defaultValue={record[dataIndex]}
            maxLength={FIELD_MAX_LENGTH.LABEL}
          />
        )}
        {(dataIndex === "description") && (
          <Input
            style={{ margin: 0, paddingBottom: 0, paddingTop: 0 }}
            onChange={(event) => onDescriptionChange(event.target.value, record, dataIndex)}
            onPressEnter={() => { save(); toggleEdit(); }}
            onBlur={() => {
              save(); toggleEdit();
            }}
            ref={inputRef}
            value={record[dataIndex]}
            defaultValue={record[dataIndex]}
            maxLength={FIELD_MAX_LENGTH.DESCRIPTION}
          />
        )}
        {(dataIndex === "deltaCollection") && (
          <Input
            style={{ margin: 0, paddingBottom: 0, paddingTop: 0 }}
            onChange={(event) => onDeltaCollectionChange(event.target.value, record, dataIndex)}
            onPressEnter={() => { save(); toggleEdit(); }}
            onBlur={() => {
              save(); toggleEdit();
            }}
            ref={inputRef}
            value={record[dataIndex]}
            defaultValue={record[dataIndex]}
            maxLength={FIELD_MAX_LENGTH.DELTA_COLLECTION}
          />
        )}
        {dataIndex === "owner" && (
          <Input
            style={{ margin: 0, paddingBottom: 0, paddingTop: 0 }}
            onChange={event => onOwnerChange(event.target.value, record, dataIndex)}
            onPressEnter={() => { save(); toggleEdit(); }}
            onBlur={() => { save(); toggleEdit(); }}
            ref={inputRef}
            value={record[dataIndex]}
            defaultValue={record[dataIndex]}
            maxLength={FIELD_MAX_LENGTH.OWNER}
          />
        )}
        {dataIndex === "whoToSolve" && (
          <Input
            style={{ margin: 0, paddingBottom: 0, paddingTop: 0 }}
            onChange={(event) => onWhoToSolveChange(event.target.value, record, dataIndex)}
            onPressEnter={() => { save(); toggleEdit(); }}
            onBlur={() => { save(); toggleEdit(); }}
            ref={inputRef}
            value={record[dataIndex]}
            defaultValue={record[dataIndex]}
            maxLength={FIELD_MAX_LENGTH.WHO_TO_SOLVE}
          />
        )}
        {dataIndex === "position" && (
          <Input
            style={{ margin: 0, paddingBottom: 0, paddingTop: 0 }}
            disabled={record["nonEditable"]}
            onChange={(event) => onPositionChange(event, record, dataIndex)}
            onBlur={() => {
              save(); toggleEdit();
            }}
            min={0}
            size={"small"}
            type={"number"}
            ref={inputRef}
            value={record[dataIndex]}
            defaultValue={record[dataIndex]}
          />
        )}
      </Form.Item>
    ) : (
      <Form.Item
        style={{ margin: 0, height: 22 }}
        name={dataIndex}
        shouldUpdate={true}
        validateStatus={getValidationStatus(record, dataIndex)}
      >
        {dataIndex == "fieldName" && (
          <Input
            onClick={toggleEdit}
            onFocus={toggleEdit}
            value={record[dataIndex]}
            disabled={record["nonEditable"]}
            style={{ margin: 0, paddingBottom: 0, paddingTop: 0 }}
            maxLength={FIELD_MAX_LENGTH.NAME}
          />
        )}
        {dataIndex == "datatype" && (
          <Select
            style={{ margin: 0, padding: 0 }}
            showSearch={true}
            showArrow={true}
            allowClear={true}
            disabled={record["nonEditable"]}
            placeholder={
              t("General.Placeholders.EnterValue", {
                lngs: i18n.languages,
                defaultValue: "Enter value",
              })!
            }
            onChange={onDatatypeChange}
            onBlur={(event: any) => {
              if (event.target.value) {
                const filteredDataTypes = DATA_TYPES.filter((x: any) => x.value.toUpperCase().indexOf(event.target.value.toUpperCase()) !== -1);
                if (filteredDataTypes && filteredDataTypes.length > 0) {
                  onDatatypeChange(filteredDataTypes[0].value)
                }
              }
            }}
            options={DATA_TYPES}
            size="small"
            value={record[dataIndex]}
            filterOption={(inputValue: any, option: any) =>
              option!.value
                .toUpperCase()
                .indexOf(inputValue.toUpperCase()) !== -1
            }
          ></Select>
        )}
        {dataIndex == "label" && (
          <Input
            onClick={toggleEdit}
            onFocus={toggleEdit}
            disabled={record["nonEditable"]}
            value={record[dataIndex]}
            style={{ margin: 0, paddingBottom: 0, paddingTop: 0 }}
            maxLength={FIELD_MAX_LENGTH.LABEL}
          />
        )}
        {dataIndex == "description" && (
          <Input
            onClick={toggleEdit}
            onFocus={toggleEdit}
            disabled={record["nonEditable"]}
            value={record[dataIndex]}
            style={{ margin: 0, paddingBottom: 0, paddingTop: 0 }}
            maxLength={FIELD_MAX_LENGTH.DESCRIPTION}
          />
        )}
        {dataIndex === "maxLength" && (
          <>
            {record["datatype"] &&
              record["datatype"]?.toLowerCase() !== "varchar" &&
              record["datatype"]?.toLowerCase() !== "nvarchar" ? (
              <Input
                value={record[dataIndex]}
                disabled
                style={{ margin: 0, paddingBottom: 0, paddingTop: 0 }}
                size={"small"}
                type={"number"}
              />
            ) : (
              <Input
                onClick={toggleEdit}
                onFocus={toggleEdit}
                disabled={record["nonEditable"]}
                value={record[dataIndex]}
                style={{ margin: 0, paddingBottom: 0, paddingTop: 0 }}
                size={"small"}
                type={"number"}
                max={record["datatype"]?.toLowerCase() === "varchar" ? FIELD_MAX_LENGTH.TYPE_VARCHAR : FIELD_MAX_LENGTH.TYPE_NVARCHAR}
              />
            )}
          </>
        )}
        {(dataIndex == "scale") && (
          <>
            {record["datatype"]?.toLowerCase() !== "numeric" &&
              record["datatype"]?.toLowerCase() !== "decimal" ? (
              <Input
                value={record[dataIndex]}
                disabled
                type="number"
                style={{ margin: 0, paddingBottom: 0, paddingTop: 0 }}
              />
            ) : (
              <Input
                onClick={toggleEdit}
                onFocus={toggleEdit}
                disabled={record["nonEditable"]}
                value={record[dataIndex]}
                style={{ margin: 0, paddingBottom: 0, paddingTop: 0 }}
                size={"small"}
                type={"number"}
              />
            )}
          </>
        )}
        {(dataIndex == "precision") && (
          <>
            {record["datatype"]?.toLowerCase() !== "numeric" &&
              record["datatype"]?.toLowerCase() !== "decimal" ? (
              <Input
                value={record[dataIndex]}
                disabled
                type="number"
                style={{ margin: 0, paddingBottom: 0, paddingTop: 0 }}
              />
            ) : (
              <Input
                onClick={toggleEdit}
                onFocus={toggleEdit}
                disabled={record["nonEditable"]}
                value={record[dataIndex]}
                style={{ margin: 0, paddingBottom: 0, paddingTop: 0 }}
                size={"small"}
                type={"number"}
              />
            )}
          </>
        )}
        {dataIndex == "autotrim" && (
          <>
            {selectedOperation == 1 || selectedOperation == 2 ? (
              <Checkbox
                className="editable-cell-value-wrap"
                style={{
                  paddingRight: 24,
                  height: 22,
                  opacity: 0.5,
                  // background: "#CCC",
                  pointerEvents: "none",
                  // width: 40,
                }}
                disabled={record["nonEditable"]}
                checked={record[dataIndex]}
                onClick={(event: any) => onAutotrimChange(event, record, dataIndex)}
              />
            ) : (
              <Checkbox
                className="editable-cell-value-wrap"
                style={{ paddingRight: 24, height: 22 }}
                disabled={record["nonEditable"]}
                checked={record[dataIndex]}
                onClick={(event: any) => onAutotrimChange(event, record, dataIndex)}
              />
            )}
          </>
        )}
        {dataIndex == "deltaCollection" && (
          <>
            <Input
              onClick={toggleEdit}
              disabled={record["nonEditable"]}
              value={record[dataIndex]}
              style={{ margin: 0, paddingBottom: 0, paddingTop: 0 }}
              maxLength={FIELD_MAX_LENGTH.DELTA_COLLECTION}
            />
          </>
        )}
        {dataIndex == "mandatory" && (
          <>
            {selectedOperation === 1 || selectedOperation === 2 ? (
              <Checkbox
                className="editable-cell-value-wrap"
                style={{
                  paddingRight: 24,
                  height: 22,
                  opacity: 0.5,
                }}
                disabled={record["nonEditable"]}
                checked={record[dataIndex]}
                onClick={(event: any) => onMandatoryChange(event, record, dataIndex)}
              />
            ) : (
              <Checkbox
                className="editable-cell-value-wrap"
                style={{ paddingRight: 24, height: 22 }}
                disabled={record["nonEditable"]}
                checked={record[dataIndex]}
                onClick={(event: any) => onMandatoryChange(event, record, dataIndex)}
              />

            )}
          </>
        )}
        {dataIndex == "typeId" && (
          <Select
            style={{ margin: 0, padding: 0 }}
            showSearch={true}
            showArrow={true}
            allowClear={true}
            disabled={record["nonEditable"]}
            placeholder={
              t("General.Placeholders.EnterValue", {
                lngs: i18n.languages,
                defaultValue: "Enter value",
              })!
            }
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            onChange={onFieldTypeChange}
            options={fieldTypes.map((type) => ({
              label: type.name,
              value: type.id,
            }
            ))}
            size="small"
            value={record[dataIndex]}
          ></Select>
        )}
        {dataIndex == "subtypeFormatId" && (
          <Select
            style={{ margin: 0, padding: 0 }}
            showSearch={true}
            showArrow={true}
            allowClear={true}
            disabled={record["nonEditable"]}
            placeholder={
              t("General.Placeholders.EnterValue", {
                lngs: i18n.languages,
                defaultValue: "Enter value",
              })!
            }
            onChange={onSubFieldTypeChange}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={fieldSubTypes
              .filter(x => x.typeId === record.typeId)
              .map(x => ({ label: x.label, value: x.id }))}
            size="small"
            value={record[dataIndex]}
          ></Select>
        )}
        {dataIndex === "owner" && (
          <Input
            style={{ margin: 0, paddingBottom: 0, paddingTop: 0 }}
            onChange={(event) => onOwnerChange(event.target.value, record, "owner")}
            ref={inputRef}
            disabled={record["nonEditable"]}
            value={record[dataIndex]}
            maxLength={FIELD_MAX_LENGTH.OWNER}
          />
        )}
        {dataIndex === "whoToSolve" && (
          <Input
            style={{ margin: 0, paddingBottom: 0, paddingTop: 0 }}
            onChange={(event) => onWhoToSolveChange(event.target.value, record, dataIndex)}
            ref={inputRef}
            disabled={record["nonEditable"]}
            value={record[dataIndex]}
            maxLength={FIELD_MAX_LENGTH.WHO_TO_SOLVE}
          />
        )}
        {dataIndex == "position" && (
          <>
            {selectedOperation === 1 ? (
              <Input
                value={record[dataIndex]}
                disabled
                style={{ margin: 0, paddingBottom: 0, paddingTop: 0, padding: "0px 2px" }}
                size={"small"}
                type={"number"}
              />
            ) : (
              <Input
                onClick={toggleEdit}
                onFocus={toggleEdit}
                disabled={record["nonEditable"]}
                value={record[dataIndex]}
                style={{ margin: 0, paddingBottom: 0, paddingTop: 0, padding: "0px 2px" }}
                size={"small"}
                type={"number"}
              />

            )}
          </>
        )}
      </Form.Item>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

type EditableTableProps = Parameters<typeof Table>[0];

interface DataType {
  key: React.Key;
  fieldName: string;
  datatype: string;
  maxLength?: number;
  autotrim?: boolean;
  precision?: number;
  scale?: number;
  owner?: string;
  whoToSolve?: string;
  label: string;
  description: string;
  deltaCollection?: string;
  mandatory?: boolean;
  position: number;
  nonEditable: boolean;
}

type ColumnTypes = Exclude<EditableTableProps["columns"], undefined>;

const EditableTable = ({
  dataSource,
  setDataSource,
  selectedEntity,
  selectedOperation,
  selectedDatabase,
  selectedSchema,
  handleClose,
  downloadRecreateTableAsExcelDrawerEnabled,
  setDownloadRecreateTableAsExcelDrawerEnabled,
  downloadRecreateTableAsEmptyExcelDrawerEnabled,
  setDownloadRecreateTableAsEmptyExcelDrawerEnabled,
  uploadRecreateTableFromExcelDrawerEnabled,
  setUploadRecreateTableFromExcelDrawerEnabled,
  uploadRecreateTableFromTableDrawerEnabled,
  setUploadRecreateTableFromTableDrawerEnabled,
  recreateTableSaveTriggered,
  setRecreateTableSaveTriggered,
  recreateTableSaveAndCloseTriggered,
  setRecreateTableSaveAndCloseTriggered,
  recreateTableFields,
  recreateTableName,
  entities,
  operations,
  selectedTable,
  isEditFieldsSaveLoading,
  setIsEditFieldsSaveLoading,
  isDirty,
  setIsDirty,
  isCheckboxChanged,
  setIsCheckboxChanged,
  sourceKeys,
  setSourceKeys,
  fieldTypes,
  fieldSubTypes,
}: {
  dataSource: any;
  setDataSource: any;
  selectedEntity: any;
  selectedOperation: any;
  selectedDatabase: any;
  selectedSchema: any;
  handleClose: any;
  downloadRecreateTableAsExcelDrawerEnabled: any;
  setDownloadRecreateTableAsExcelDrawerEnabled: any;
  downloadRecreateTableAsEmptyExcelDrawerEnabled: any;
  setDownloadRecreateTableAsEmptyExcelDrawerEnabled: any;
  uploadRecreateTableFromExcelDrawerEnabled: any;
  setUploadRecreateTableFromExcelDrawerEnabled: any;
  uploadRecreateTableFromTableDrawerEnabled: any;
  setUploadRecreateTableFromTableDrawerEnabled: any;
  recreateTableSaveTriggered: any;
  setRecreateTableSaveTriggered: any;
  recreateTableSaveAndCloseTriggered: any;
  setRecreateTableSaveAndCloseTriggered: any;
  recreateTableFields: any;
  recreateTableName: any;
  entities: any;
  operations: any;
  selectedTable: any;
  isEditFieldsSaveLoading: any;
  setIsEditFieldsSaveLoading: any;
  isDirty: any;
  setIsDirty: any;
  isCheckboxChanged: any;
  setIsCheckboxChanged: any;
  sourceKeys: any;
  setSourceKeys: any;
  fieldTypes: FieldType[];
  fieldSubTypes: FieldSubType[];
}) => {
  const [uploadForm] = Form.useForm();

  const { t, i18n } = useTranslation("translations");
  const [tables, setTables] = useState<any>([]);

  const [isUploadLoading, setIsUploadLoading] = useState<boolean>(false);

  const [originalDataSource, setOriginalDataSource] = useState(dataSource);

  useEffect(() => {
    if (selectedEntity && selectedOperation) {
      const model: GetTablesRequest = {
        entityId: selectedEntity,
        operationId: selectedOperation,
        groupCode: "",
      };

      TablesService.getTables(model).then((response: any) => {
        const data = response.data.sort(function (a: any, b: any) {
          if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
          if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
          return 0;
        });
        setTables(data);
      });
    }
  }, [selectedEntity, selectedOperation]);

  useEffect(() => {
    const newDataSource = dataSource.filter((x: any) => !x.nonEditable);

    if (selectedOperation == 2) {
      newDataSource.push(...[
        {
          key: 0,
          fieldName: "Code",
          datatype: "nvarchar",
          maxLength: 250,
          label: "",
          description: "",
          deltaCollection: "",
          mandatory: false,
          position: 1,
          nonEditable: true,
        },
        {
          key: 1,
          fieldName: "GroupCode",
          datatype: "nvarchar",
          maxLength: 50,
          label: "",
          description: "",
          deltaCollection: "",
          mandatory: false,
          position: 2,
          nonEditable: true,
        },
        {
          key: 2,
          fieldName: "Run_Date",
          datatype: "int",
          label: "",
          description: "",
          deltaCollection: "ALL",
          mandatory: false,
          position: 3,
          nonEditable: true,
        },
        {
          key: 3,
          fieldName: "DeltaStatus",
          datatype: "int",
          label: "",
          description: "",
          deltaCollection: "",
          mandatory: false,
          position: 4,
          nonEditable: true,
        },
        {
          key: 4,
          fieldName: "MOD_DT_ALL",
          datatype: "int",
          label: "",
          description: "",
          deltaCollection: "",
          mandatory: false,
          position: 5,
          nonEditable: true,
        },
        {
          key: 5,
          fieldName: "SourceData",
          datatype: "nvarchar",
          maxLength: 100,
          label: "",
          description: "",
          deltaCollection: "",
          mandatory: false,
          position: 6,
          nonEditable: true,
        },
      ]);

      setDataSource(newDataSource.sort((a: any, b: any) => b.nonEditable - a.nonEditable));
    }
    if (selectedOperation == 3) {
      newDataSource.push(...[
        {
          key: 0,
          fieldName: "Code",
          datatype: "nvarchar",
          maxLength: 250,
          label: "",
          description: "",
          deltaCollection: "",
          mandatory: false,
          owner: "",
          whoToSolve: "",
          position: 1,
          nonEditable: true,
        },
        {
          key: 1,
          fieldName: "GroupCode",
          datatype: "nvarchar",
          maxLength: 50,
          label: "",
          description: "",
          deltaCollection: "",
          mandatory: false,
          owner: "",
          whoToSolve: "",
          position: 2,
          nonEditable: true,
        },
        {
          key: 2,
          fieldName: "Environment",
          datatype: "nvarchar",
          maxLength: 50,
          label: "",
          description: "",
          deltaCollection: "ALL",
          mandatory: false,
          owner: "",
          whoToSolve: "",
          position: 3,
          nonEditable: true,
        },
      ]);
    }
    if (selectedOperation == 5) {
      newDataSource.push(...[
        {
          key: 0,
          fieldName: "Code",
          datatype: "nvarchar",
          maxLength: 250,
          label: "",
          description: "",
          deltaCollection: "",
          mandatory: false,
          position: 1,
          nonEditable: true,
        },
        {
          key: 1,
          fieldName: "GroupCode",
          datatype: "nvarchar",
          maxLength: 50,
          label: "",
          description: "",
          deltaCollection: "",
          mandatory: false,
          position: 2,
          nonEditable: true,
        },
        {
          key: 2,
          fieldName: "System",
          datatype: "nvarchar",
          maxLength: 50,
          label: "",
          description: "",
          deltaCollection: "ALL",
          mandatory: false,
          position: 3,
          nonEditable: true,
        },
        {
          key: 3,
          fieldName: "Environment",
          datatype: "nvarchar",
          maxLength: 50,
          label: "",
          description: "",
          deltaCollection: "ALL",
          mandatory: false,
          position: 3,
          nonEditable: true,
        },
      ]);

      setDataSource(newDataSource.sort((a: any, b: any) => b.nonEditable - a.nonEditable));
    }
  }, [selectedOperation]);

  useEffect(() => {
    if (recreateTableFields) {
      let newDataSource = dataSource;
      if (newDataSource[newDataSource.length - 1] && newDataSource[newDataSource.length - 1].fieldName === "") {
        newDataSource.pop();
      }

      newDataSource.push(...recreateTableFields
        .filter((x: any) => {
          if (x.fieldName && ["code", "groupcode", "run_date", "deltastatus", "mod_dt_all", "sourcedata"].indexOf(x.fieldName.toLowerCase()) > -1) {
            return false;
          }

          if (x.name && ["code", "groupcode", "run_date", "deltastatus", "mod_dt_all", "sourcedata"].indexOf(x.name.toLowerCase()) > -1) {
            return false;
          }

          return true;
        })
        .map((x: any, index: any) => {
          if (selectedOperation == 2) {
            let model = {
              key: x.key ?? index,
              name: x.name,
              fieldName: x.fieldName ? x.fieldName : x.name,
              datatype: x.datatype,
              maxLength: x.maxLength,
              label: x.label,
              description: x.description,
              deltaCollection: x.deltaCollection,
              precision: x.precision,
              scale: x.scale,
              mandatory: x.isMandatory,
              position: x.position,
              nonEditable: x.fieldName ? ["code", "groupcode", "run_date", "deltastatus", "mod_dt_all", "sourcedata"].indexOf(x.fieldName.toLowerCase()) > -1 : ["code", "groupcode", "run_date", "deltastatus", "mod_dt_all", "sourcedata"].indexOf(x.name?.toLowerCase()) > -1,
            };
            return model;
          }
          if (selectedOperation == 3) {
            let model = {
              key: x.key ?? index,
              name: x.name,
              fieldName: x.fieldName ? x.fieldName : x.name,
              datatype: x.datatype,
              maxLength: x.maxLength,
              label: x.label,
              description: x.description,
              deltaCollection: x.deltaCollection,
              precision: x.precision,
              scale: x.scale,
              mandatory: x.isMandatory,
              position: x.position,
              nonEditable: x.fieldName ? ["code", "groupcode", "environment"].indexOf(x.fieldName.toLowerCase()) > -1 : ["code", "groupcode", "environment"].indexOf(x.name.toLowerCase()) > -1,
            };
          }
          if (selectedOperation == 5) {
            let model = {
              key: x.key ?? index,
              name: x.name,
              fieldName: x.fieldName ? x.fieldName : x.name,
              datatype: x.datatype,
              maxLength: x.maxLength,
              label: x.label,
              description: x.description,
              deltaCollection: x.deltaCollection,
              precision: x.precision,
              scale: x.scale,
              mandatory: x.isMandatory,
              position: x.position,
              nonEditable: x.fieldName ? ["code", "groupcode", "system", "environment"].indexOf(x.fieldName.toLowerCase()) > -1 : ["code", "groupcode", "system"].indexOf(x.name.toLowerCase()) > -1,
            };
            return model;
          }
          return x;
        }));

      const maxPosition = Math.max(...newDataSource.map((o: any) => o.position))
      const maxKey = Math.max(...newDataSource.map((o: any) => o.key ?? 0))

      newDataSource.push(
        {
          key: maxKey + 1,
          name: "",
          fieldName: "",
          datatype: "",
          maxLength: null,
          autotrim: false,
          precision: null,
          scale: null,
          label: "",
          owner: "",
          whoToSolve: "",
          description: "",
          deltaCollection: "ALL",
          mandatory: false,
          position: maxPosition + 1,
          nonEditable: false,
        }
      );

      setDataSource([...newDataSource]);
    }
  }, [recreateTableFields]);

  useEffect(() => {
    let close = false;
    let update = false;

    if (recreateTableSaveTriggered || recreateTableSaveAndCloseTriggered) {
      update = true;
    }
    if (recreateTableSaveAndCloseTriggered) {
      close = true;
    }

    if (update) {
      setIsEditFieldsSaveLoading(true);

      const model: any = {
        tableId: selectedTable?.id,
        tableName: recreateTableName,
        database: selectedDatabase,
        schema: selectedSchema,
        entityId: selectedEntity,
        operationId: selectedOperation,
        recreateTableFields: dataSource.filter((x: any) => !x.nonEditable).filter((x: any) => x.fieldName != "").map((x: any) => ({
          ...x,
          mandatory: x.mandatory,

        })),
      };
      TablesService.recreateTable(model).then((response: any) => {
        NotificationsUtil.openNotificationDialog({
          description: "Table & fields created successfully.",
          type: NOTIFICATION_TYPES.SUCCESS,
          logError: false,
        });
        setIsDirty(false);
        if (close) {
          handleClose();
        } else {
          const fieldsModel: GetFieldsRequest = {
            tableId: selectedTable?.id,
          };
          TablesService.getFields(fieldsModel).then((fieldsResponse: any) => {
            console.log(fieldsResponse);
            const fieldsData = fieldsResponse.data;
            setDataSource(
              fieldsData.map((x: any) => {
                return {
                  ...x,
                  fieldName: x.name,
                  autotrim: x.isTruncate,
                  mandatory: x.isMandatory,
                  owner: x.owner,
                  whoToSolve: x.whoToSolve,
                };
              })
            );
          });
        }
        setIsEditFieldsSaveLoading(false);
        setIsCheckboxChanged(false);
      }).catch((error: any) => {
        setIsEditFieldsSaveLoading(false);
        if (error.response.status === 400) {
          const description = "Some cell(s) have missing or incorrect input. Please review all cells highlighted in red and try again.";

          NotificationsUtil.openNotificationDialog({
            description: description,
            type: NOTIFICATION_TYPES.ERROR,
            logError: true,
          });
        } else {
          NotificationsUtil.openNotificationDialog({
            description: error.response.data,
            type: NOTIFICATION_TYPES.ERROR,
            logError: true,
          });
        }
      }).finally(() => {
        setIsEditFieldsSaveLoading(false);
        setRecreateTableSaveAndCloseTriggered(false);
        setRecreateTableSaveTriggered(false);
      });
    }
  }, [recreateTableSaveTriggered, recreateTableSaveAndCloseTriggered])

  const [drawerTitle, setDrawerTitle] = useState<string>("");
  const [drawerAction, setDrawerAction] = useState<RowAction>();
  const [openDrawer, setOpenDrawer] = useState(false);

  const [selectedUploadFile, setSelectedUploadFile] = useState<any>(null);
  const [selectedUploadTable, setSelectedUploadTable] = useState<any>(null);
  const [selectedImportEntity, setSelectedImportEntity] = useState<any>(null);
  const [selectedImportOperation, setSelectedImportOperation] = useState<any>(null);

  const handleDelete = (key: React.Key) => {
    const newData = dataSource.filter((item: any) => item.key !== key);
    setDataSource(newData);
  };

  const handleCheckboxChange = (e: CheckboxChangeEvent, key: string) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => key === item.key);
    if (index > -1) {
      const item = newData[index];
      newData.splice(index, 1, { ...item, isKey: e.target.checked });
      const newKeys = newData.filter((x: any) => x.isKey).map((x: any) => x.fieldName);
      const isChanged = newKeys.length !== sourceKeys.length || newKeys.some((x: any) => !sourceKeys.includes(x));
      setIsCheckboxChanged(isChanged);
      setDataSource(newData);
    }
  };

  const showDrawer = (action: RowAction) => {
    setDrawerAction(action);
    switch (action) {
      case RowAction.UploadFromExcel:
        setDrawerTitle(
          // t(
          //   "Page.Configuration.TableSetup.Tabs.Translations.Labels.UploadTranslations",
          //   { lngs: i18n.languages, defaultValue: "Upload translation" }
          // )!
          "Import from .xlsx template"
        );
        break;
      case RowAction.UploadFromTable:
        setSelectedImportEntity(null);
        setSelectedImportOperation(null);
        setTables([]);
        setDrawerTitle(
          // t(
          //   "Page.Configuration.TableSetup.Tabs.Translations.Labels.DownloadTranslations",
          //   { lngs: i18n.languages, defaultValue: "Download translations" }
          // )!
          "Import from other table"
        );
        break;
      default:
        break;
    }
    setOpenDrawer(true);
  };

  const onClose = () => {
    setOpenDrawer(false);
    handleSelectedImportEntityChange(undefined);
    setUploadRecreateTableFromExcelDrawerEnabled(false);
    setUploadRecreateTableFromTableDrawerEnabled(false);
    setSelectedUploadFile(null);
  };

  const downloadExcel = (empty: boolean) => {
    const model: any = {
      operationId: selectedOperation,
      recreateTableFields: empty ? [] : dataSource,
    };

    if (model.recreateTableFields.length > 0) {
      model.recreateTableFields.splice(-1, 1);
    }

    TablesService.downloadRecreateTableExcel(model).then((response: any) => {
      let url = window.URL.createObjectURL(new Blob([response.data]));
      saveAs(url, "table.xlsx");
    });
  };

  const defaultColumns: (ColumnTypes[number] & {
    editable?: boolean;
    visible?: boolean;
    dataIndex: string;
  })[] = [
      {
        title: "FieldName",
        dataIndex: "fieldName",
        width: selectedOperation === 2 ? '17%' : '20%',
        editable: true,
        visible: true,
      },
      {
        title: "Datatype",
        dataIndex: "datatype",
        width: 100,
        editable: true,
        visible: true,
      },
      {
        title: "MaxLength",
        dataIndex: "maxLength",
        width: 60,
        editable: true,
        visible: true,
      },
      {
        title: "Autotrim",
        dataIndex: "autotrim",
        editable: true,
        visible: true,
        width: 55,
        align: "right",
        // render: (_, record: any) => <Checkbox disabled={record.nonEditable} />,
      },
      {
        title: "Precision",
        dataIndex: "precision",
        width: 55,
        editable: true,
        visible: true,
      },
      {
        title: "Scale",
        dataIndex: "scale",
        width: 50,
        editable: true,
        visible: true,
      },
      {
        title: "Label",
        dataIndex: "label",
        width: 80,
        editable: true,
        visible: true,
      },
      {
        title: "Description",
        dataIndex: "description",
        width: selectedOperation === 2 ? 80 : 120,
        editable: true,
        visible: true,
      },
      {
        title: "DeltaCollection",
        dataIndex: "deltaCollection",
        width: 80,
        editable: true,
        visible: selectedOperation === 2,
      },
      {
        title: 'Is Key',
        dataIndex: 'isKey',
        key: 'isKey',
        width: 40,
        align: 'center',
        visible: selectedOperation === 1,
        render: (text: string, record: any) => (
          <Checkbox
            checked={record.isKey}
            onChange={(e) => handleCheckboxChange(e, record.key)}
          />
        ),
      },
      {
        title: "Mandatory",
        dataIndex: "mandatory",
        editable: true,
        visible: true,
        align: "right",
        width: 50,
        // render: (_, record: any) => <Checkbox />,
      },
      {
        title: "Owner",
        dataIndex: "owner",
        editable: true,
        width: 50,
        align: "center",
        visible: selectedOperation === 3,
      },
      {
        title: "Who To Solve",
        dataIndex: "whoToSolve",
        editable: true,
        width: 50,
        align: "center",
        visible: selectedOperation === 3,
      },
      {
        title: "Type",
        dataIndex: "typeId",
        width: 80,
        visible: false,
        editable: true,
      },
      {
        title: "Sub Type",
        dataIndex: "subtypeFormatId",
        width: 100,
        visible: false,
        editable: true,
      },
      {
        title: "Position",
        dataIndex: "position",
        width: 50,
        editable: true,
        visible: true,
        align: "center",
      },
      {
        title: "Actions",
        dataIndex: "actions",
        width: 50,
        align: "center",
        visible: true,
        render: (_, record: any) =>
          dataSource.length >= 1 && !record["nonEditable"] ? (
            <Popconfirm
              title="Sure to delete?"
              onConfirm={() => handleDelete(record.key)}
            >
              <DeleteOutlined style={{ color: "#1e1e85" }} />
            </Popconfirm>
          ) : null,
      },
    ];

  const handleSave = (row: DataType) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,

    });

    // const maxKey = Math.max(...dataSource.map((o:any) => parseInt(o.key.toString())));
    if (newData[newData.length - 1].fieldName !== "") {
      const maxPosition = Math.max(...dataSource.map((o: any) => o.position))
      const maxKey = Math.max(...dataSource.map((o: any) => o.key ?? 0))
      const newRow: DataType = {
        key: maxKey + 1,
        fieldName: "",
        datatype: "",
        maxLength: 0,
        autotrim: false,
        precision: 0,
        scale: 0,
        label: "",
        description: "",
        deltaCollection: "ALL",
        mandatory: false,
        owner: "",
        whoToSolve: "",
        position: maxPosition + 1,
        nonEditable: false,
      };
      newData.push(newRow);
    }
    setDataSource([...newData]);
    setIsDirty(true);
  };

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  const columns = defaultColumns.filter((col: any) =>
    col.visible
  ).map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: DataType) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        fieldTypes: fieldTypes,
        fieldSubTypes: fieldSubTypes,
        title: col.title,
        handleSave,
      }),
    };
  });

  const onUploadFile = () => {
    const model: any = {
      tableId: selectedTable?.id,
      file: selectedUploadFile,
    };

    setIsEditFieldsSaveLoading(true);
    setIsUploadLoading(true);
    TablesService.importRecreateTableFromExcel(model)
      .then((response: any) => {
        let newDataSource = [...dataSource];
        if (newDataSource[newDataSource.length - 1] && newDataSource[newDataSource.length - 1].fieldName === "") {
          newDataSource.pop();
        }

        for (let i = 0; i < response.data.length; i++) {
          if (response.data[i].fieldName.length <= 0) {
            continue; // skip empty field
          }
          if (selectedOperation == 2 && ["code", "groupcode", "run_date", "deltastatus", "mod_dt_all", "sourcedata"].indexOf(response.data[i].fieldName.toLowerCase()) > -1) {
            continue; // ignore technical fields
          }
          if ((selectedOperation == 3) && ["code", "groupcode", "environment"].indexOf(response.data[i].fieldName.toLowerCase()) > -1) {
            continue; // ignore technical fields
          }
          if ((selectedOperation == 5) && ["code", "groupcode", "system", "environment"].indexOf(response.data[i].fieldName.toLowerCase()) > -1) {
            continue; // ignore technical fields
          }

          let existingObjectIndex = newDataSource.findIndex((x: any) => x.fieldName.toLowerCase() === response.data[i].fieldName.toLowerCase());
          if (existingObjectIndex > -1) {
            newDataSource[existingObjectIndex] = response.data[i]; // update existing
          } else {
            let newRecord = response.data[i];
            if (newDataSource.findIndex((x: any) => x.position === newRecord.position) !== -1) {
              newRecord.position = Math.max(...newDataSource.map((o: any) => o.position)) + 1
              newRecord.key = Math.max(...newDataSource.map((o: any) => o.key ?? 0)) + 1
            }
            newDataSource.push(newRecord); // create new
          }
        }

        setDataSource([...newDataSource]);

        setIsUploadLoading(false);
        setIsEditFieldsSaveLoading(false);
        setSelectedUploadFile(null);
        onClose();
        NotificationsUtil.openNotificationDialog({
          description: t(
            "Page.Configuration.TableSetup.Tabs.Translations.Notifications.Translation.UploadSuccessDescription",
            {
              lngs: i18n.languages,
              defaultValue: "Excel upload succeeded.",
            }
          )!,
          type: NOTIFICATION_TYPES.SUCCESS,
          logError: false,
        });
      })
      .catch((error: any) => {
        setIsUploadLoading(false);
        setIsEditFieldsSaveLoading(false);
        NotificationsUtil.openNotificationDialog({
          description: error?.response?.data?.Message,
          type: NOTIFICATION_TYPES.ERROR,
          logError: true,
        });
      });
  };

  const onUploadTable = () => {
    const model: any = {
      fromTableId: selectedUploadTable,
    };

    setIsUploadLoading(true);
    setIsEditFieldsSaveLoading(true);
    TablesService.importRecreateTableFromTable(model)
      .then((response: any) => {
        let newDataSource: any = dataSource.map(x => ({
          ...x,
          datatype: x.datatype?.toLowerCase()
        }));
        if (newDataSource[newDataSource.length - 1].fieldName === "") {
          newDataSource.pop();
        }

        const data = response.data;

        for (let i = 0; i < data.length; i++) {
          if ((selectedOperation == 2 || selectedImportOperation == 2) && ["code", "groupcode", "run_date", "deltastatus", "mod_dt_all", "sourcedata"].indexOf(data[i].fieldName.toLowerCase()) > -1) {
            continue; // ignore technical fields
          }
          if ((selectedOperation == 3 || selectedImportOperation == 3) && ["code", "groupcode", "environment"].indexOf(data[i].fieldName.toLowerCase()) > -1) {
            continue; // ignore technical fields
          }
          if ((selectedOperation == 5 || selectedImportOperation == 5) && ["code", "groupcode", "system", "environment"].indexOf(data[i].fieldName.toLowerCase()) > -1) {
            continue; // ignore technical fields
          }

          let existingObjectIndex = newDataSource.findIndex((x: any) => x.fieldName ? x.fieldName.toLowerCase() === response.data[i].fieldName.toLowerCase() : x.name?.toLowerCase() === response.data[i].fieldName.toLowerCase());
          let existingObject = newDataSource[existingObjectIndex];
          if (existingObjectIndex > -1) {
            newDataSource[existingObjectIndex] = response.data[i]; // update existing
            newDataSource[existingObjectIndex].id = existingObject.id;
          } else {
            let newRecord = response.data[i];
            if (newDataSource.findIndex((x: any) => x.position === newRecord.position)) {
              newRecord.position = Math.max(...newDataSource.map((o: any) => o.position)) + 1
              newRecord.key = Math.max(...newDataSource.map((o: any) => o.key ?? 0)) + 1
            }
            newDataSource.push(newRecord); // create new
          }
        }

        setIsUploadLoading(false);
        setIsEditFieldsSaveLoading(false);
        setDataSource([...newDataSource]);
        onClose();
        NotificationsUtil.openNotificationDialog({
          description: t(
            "Page.Configuration.TableSetup.Tabs.Translations.Notifications.Translation.UploadSuccessDescription",
            {
              lngs: i18n.languages,
              defaultValue: "Table upload succeeded.",
            }
          )!,
          type: NOTIFICATION_TYPES.SUCCESS,
          logError: false,
        });
      })
      .catch((error: any) => {
        setIsUploadLoading(false);
        setIsEditFieldsSaveLoading(false);
        NotificationsUtil.openNotificationDialog({
          description: error.response.data,
          type: NOTIFICATION_TYPES.ERROR,
          logError: true,
        });
      });
  };

  const handleUploadFileChange = (value: UploadChangeParam<UploadFile>) => {
    if (value.fileList.length === 0) {
      setSelectedUploadFile(null);
    } else {
      setSelectedUploadFile(value?.fileList[0].originFileObj);
      // const file: any = value?.fileList[0].originFileObj;
      // TranslationService.uploadTranslations(file);
    }
  };

  const handleSelectedImportEntityChange = (value: number) => {
    setSelectedImportEntity(value);
    handleSelectedImportOperationChange(undefined);
    uploadForm.setFieldsValue({ entity: value });
  }

  const handleSelectedImportOperationChange = (value: number) => {
    setSelectedImportOperation(value);
    handleSelectedTableChange(undefined);
    uploadForm.setFieldsValue({ operation: value });
  }

  const handleSelectedTableChange = (value: number) => {
    setSelectedUploadTable(value);
    uploadForm.setFieldsValue({ fromTable: value });
  };

  useEffect(() => {
    if (selectedImportEntity && selectedImportOperation) {
      const model: GetTablesRequest = {
        entityId: selectedImportEntity,
        operationId: selectedImportOperation,
        groupCode: "",
      };
      TablesService.getTables(model).then((response: any) => {
        const data = response.data.sort(function (a: any, b: any) {
          if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
          if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
          return 0;
        });
        setTables(data);
      });
    }
  }, [selectedImportEntity, selectedImportOperation])

  const preventRequest = () => false;
  const customRequest = (options: any) => {
    // options?.onSuccess?("ok")
  };

  useEffect(() => {
    if (downloadRecreateTableAsExcelDrawerEnabled) {
      downloadExcel(false);
      setDownloadRecreateTableAsExcelDrawerEnabled(false);
      return;
    }
    if (downloadRecreateTableAsEmptyExcelDrawerEnabled) {
      downloadExcel(true);
      setDownloadRecreateTableAsEmptyExcelDrawerEnabled(false);
      return;
    }
  }, [
    downloadRecreateTableAsExcelDrawerEnabled,
    downloadRecreateTableAsEmptyExcelDrawerEnabled,
  ]);

  useEffect(() => {
    if (uploadRecreateTableFromExcelDrawerEnabled) {
      showDrawer(RowAction.UploadFromExcel);
    }
  }, [uploadRecreateTableFromExcelDrawerEnabled]);

  useEffect(() => {
    if (uploadRecreateTableFromTableDrawerEnabled) {
      showDrawer(RowAction.UploadFromTable);
    }
  }, [uploadRecreateTableFromTableDrawerEnabled]);

  useEffect(() => {
    if (dataSource) {
      const newDataSource = dataSource.map((x: any) => {
        return {
          ...x,
          mandatory: x.mandatory,
          nonEditable: (selectedOperation == 2 && (x.fieldName ? ["code", "groupcode", "run_date", "deltastatus", "mod_dt_all", "sourcedata"].indexOf(x.fieldName.toLowerCase()) > -1 : ["code", "groupcode", "run_date", "deltastatus", "mod_dt_all", "sourcedata"].indexOf(x.name?.toLowerCase()) > -1)) ||
            ((selectedOperation == 3) && (x.fieldName ? ["code", "groupcode", "environment"].indexOf(x.fieldName.toLowerCase()) > -1 : ["code", "groupcode", "environment"].indexOf(x.name?.toLowerCase()) > -1)) ||
            ((selectedOperation == 5) && (x.fieldName ? ["code", "groupcode", "system", "environment"].indexOf(x.fieldName.toLowerCase()) > -1 : ["code", "groupcode", "system"].indexOf(x.name?.toLowerCase()) > -1)),
        }
      })

      for (let i = 0; i < newDataSource.length; i++) {
        if (!newDataSource[i].key) {
          const maxKey = Math.max(...newDataSource.map((o: any) => o.key ?? 0))
          newDataSource[i].key = maxKey + 1;
        }
      }

      if (newDataSource[newDataSource.length - 1].fieldName !== "") {
        const maxPosition = Math.max(...newDataSource.map((o: any) => o.position))
        const maxKey = Math.max(...newDataSource.map((o: any) => o.key ?? 0))
        newDataSource.push(
          {
            key: maxKey + 1,
            fieldName: "",
            datatype: "",
            maxLength: null,
            autotrim: false,
            precision: null,
            scale: null,
            label: "",
            description: "",
            deltaCollection: "ALL",
            mandatory: false,
            owner: "",
            whoToSolve: "",
            position: maxPosition + 1,
            nonEditable: false,
          }
        );
      }

      const current = JSON.stringify(dataSource);
      const def = JSON.stringify(newDataSource)

      if (current !== def) {
        setDataSource(newDataSource);
      }
    }
  }, [dataSource])

  return (
    <div
      style={{
        height: "calc(100vh - 150px)",
        overflow: "auto",
        scrollbarWidth: "thin"
      }}
    >
      {/* <Button onClick={handleAdd} type="primary" style={{ marginBottom: 16 }}>
        Add a row
      </Button> */}
      <Table
        rowKey="index"
        className="editable-table"
        size="middle"
        components={components}
        rowClassName={() => "editable-row"}
        bordered
        dataSource={dataSource}
        columns={columns as ColumnTypes}
        pagination={false}
        sticky={true}
      />
      <Drawer
        width={"600px"}

        getContainer={false}
        style={{ position: "absolute", overflow: "hidden" }}
        title={drawerTitle}
        placement={DRAWER_PLACEMENTS.BOTTOM}
        onClose={onClose}
        visible={openDrawer}
        closable={false}
        extra={
          <Space>
            {RowAction?.UploadFromExcel === drawerAction && (
              <>
                <Button
                  disabled={!selectedUploadFile}
                  className={!selectedUploadFile ? "" : "active-btn"}
                  onClick={onUploadFile}
                  loading={isUploadLoading}
                  icon={<CheckOutlined />}
                  size="small"
                >
                  {/* {t("General.Labels.StartUpload", {
                      lngs: i18n.languages,
                      defaultValue: "Start upload",
                    })} */}
                  Start upload
                </Button>
                <Button
                  type="ghost"
                  onClick={onClose}
                  icon={<CloseOutlined />}
                  size="small"
                />
              </>
            )}
            {RowAction?.UploadFromTable === drawerAction && (
              <>
                <Button
                  disabled={!selectedUploadTable}
                  className={!selectedUploadTable ? "" : "active-btn"}
                  onClick={onUploadTable}
                  loading={isUploadLoading}
                  icon={<CheckOutlined />}
                  size="small"
                >
                  {/* {t("General.Labels.StartUpload", {
                      lngs: i18n.languages,
                      defaultValue: "Start upload",
                    })} */}
                  Start upload
                </Button>
                <Button
                  type="ghost"
                  onClick={onClose}
                  icon={<CloseOutlined />}
                  size="small"
                />
              </>
            )}
          </Space>
        }
      >
        {RowAction.UploadFromTable === drawerAction && (
          <Form
            layout="vertical"
            labelCol={{ span: 12 }}
            form={uploadForm}
          >
            <Form.Item
              name="entity"
              style={{ width: "300px" }}
              label={t(
                "Page.Configuration.TableSetup.Tabs.TableSetup.RecreateTable.Labels.Entity",
                {
                  lngs: i18n.languages,
                  defaultValue: "Entity:",
                }
              )}
              tooltip={t(
                "Page.Configuration.TableSetup.Tabs.TableSetup.RecreateTable.Tooltips.Entity",
                { lngs: i18n.languages, defaultValue: "" }
              )}
            >
              <Select
                placeholder={t(
                  "Page.Configuration.TableSetup.Tabs.TableSetup.RecreateTable.Placeholders.Entity",
                  {
                    lngs: i18n.languages,
                    defaultValue: "Entity",
                  }
                )}
                loading={entities.length === 0}
                onChange={handleSelectedImportEntityChange}
                onClear={() => handleSelectedImportOperationChange(undefined)}
                showSearch={true}
                showArrow={true}
                allowClear={true}
                value={selectedImportEntity}
                options={entities.map((x: any) => ({
                  label: x.name,
                  value: x.id,
                }))}
                filterSort={(optionA, optionB) =>
                  (optionA!.label as unknown as string)
                    .toLowerCase()
                    .localeCompare(
                      (optionB!.label as unknown as string).toLowerCase()
                    )
                }
                filterOption={(inputValue: any, option: any) =>
                  option!.label
                    .toUpperCase()
                    .indexOf(inputValue.toUpperCase()) !== -1
                }
              ></Select>
            </Form.Item>
            <Form.Item
              name="operation"
              style={{ width: "300px" }}
              label={t(
                "Page.Configuration.TableSetup.Tabs.TableSetup.RecreateTable.Labels.Operation",
                {
                  lngs: i18n.languages,
                  defaultValue: "Operation:",
                }
              )}
              tooltip={t(
                "Page.Configuration.TableSetup.Tabs.TableSetup.RecreateTable.Tooltips.Operation",
                { lngs: i18n.languages, defaultValue: "" }
              )}
            >
              <Select
                placeholder={t(
                  "Page.Configuration.TableSetup.Tabs.TableSetup.RecreateTable.Placeholders.Operation",
                  {
                    lngs: i18n.languages,
                    defaultValue: "Operation",
                  }
                )}
                loading={operations.length === 0}
                onChange={handleSelectedImportOperationChange}
                onClear={() => handleSelectedTableChange(undefined)}
                showSearch={true}
                showArrow={true}
                allowClear={true}
                value={selectedImportOperation}
                options={operations.map((x: any) => ({
                  label: x.name,
                  value: x.id,
                }))}
                filterSort={(optionA: any, optionB: any) =>
                  optionA.value! < optionB.value! ? -1 : 1
                }
                filterOption={(inputValue: any, option: any) =>
                  option!.label
                    .toUpperCase()
                    .indexOf(inputValue.toUpperCase()) !== -1
                }
              ></Select>
            </Form.Item>
            <Form.Item
              name="fromTable"
              style={{ width: "300px" }}
              label={t(
                "Page.Configuration.TableSetup.Tabs.TableSetup.RecreateTable.Labels.FromTable",
                {
                  lngs: i18n.languages,
                  defaultValue: "From table:",
                }
              )}
              tooltip={t(
                "Page.Configuration.TableSetup.Tabs.TableSetup.RecreateTable.Tooltips.FromTable",
                { lngs: i18n.languages, defaultValue: "" }
              )}
            >
              <Select
                placeholder={t(
                  "Page.Configuration.TableSetup.Tabs.TableSetup.RecreateTable.Placeholders.FromTable",
                  {
                    lngs: i18n.languages,
                    defaultValue: "From table",
                  }
                )}
                loading={tables.length === 0}
                disabled={!selectedImportEntity || !selectedImportOperation}
                onChange={handleSelectedTableChange}
                showSearch={true}
                showArrow={true}
                allowClear={true}
                value={selectedUploadTable}
                options={tables.map((x: any) => ({
                  label: x.name,
                  value: x.id,
                }))}
                filterSort={(optionA: any, optionB: any) =>
                  optionA.value! < optionB.value! ? -1 : 1
                }
                filterOption={(inputValue: any, option: any) =>
                  option!.label
                    .toUpperCase()
                    .indexOf(inputValue.toUpperCase()) !== -1
                }
              ></Select>
            </Form.Item>
          </Form>
        )}
        {RowAction.UploadFromExcel === drawerAction && (
          <Form
            layout="vertical"
            labelCol={{ span: 12 }}
          // form={drawerForm}
          >
            <Form.Item
              label={
                // t(
                // "Page.Configuration.TableSetup.Tabs.Translations.Labels.Translations",
                // {
                //   lngs: i18n.languages,
                //   defaultValue: "Translations:",
                // })
                "File:"
              }
              tooltip={
                //   t(
                //   "Page.Configuration.TableSetup.Tabs.Translations.Tooltips.Translations",
                //   { lngs: i18n.languages, defaultValue: "" }
                // )}
                ""
              }
            >
              <Upload
                fileList={selectedUploadFile ? [selectedUploadFile] : []}
                onChange={handleUploadFileChange}
                beforeUpload={preventRequest}
                customRequest={customRequest}
                accept=".xls,.xlsx"
              >
                <Button loading={isUploadLoading} icon={<EditOutlined />} size="small" type="ghost">
                  {" Upload"}
                  {/* {t("General.Labels.Upload", {
                        lngs: i18n.languages,
                        defaultValue: "Upload",
                      })} */}
                </Button>
              </Upload>
            </Form.Item>
          </Form>
        )}
      </Drawer>
    </div>
  );
};

export default EditableTable;
